import { motion, useScroll, useTransform } from 'framer-motion';
import React from 'react';

const Hero = () => {
    const { scrollY } = useScroll();
    const y1 = useTransform(scrollY, [0, 500], [0, 150]);
    const y2 = useTransform(scrollY, [0, 500], [0, -100]);
    const opacity = useTransform(scrollY, [0, 300], [1, 0]);

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-wedding-blush">
            {/* Parallax Background Image */}
            <motion.div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1920&q=80")',
                    y: y1
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/70 to-white/80 backdrop-blur-[1px]"></div>
            </motion.div>

            {/* Animated Geometric Shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute top-20 right-20 w-40 h-40 border border-wedding-primary/20"
                    animate={{
                        rotate: [0, 180, 360],
                        scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    className="absolute bottom-32 left-20 w-32 h-32 rounded-full border border-wedding-accent/20"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{ duration: 15, repeat: Infinity }}
                />
                <motion.div
                    className="absolute top-1/2 left-10 w-24 h-24 border border-wedding-primary/15"
                    animate={{
                        rotate: [0, -180, -360],
                    }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                />
            </div>

            {/* Floating Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(30)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-wedding-primary/30 rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            y: [0, -30, 0],
                            opacity: [0, 1, 0],
                            scale: [0, 1, 0]
                        }}
                        transition={{
                            duration: 3 + Math.random() * 2,
                            repeat: Infinity,
                            delay: Math.random() * 5,
                            ease: "easeInOut"
                        }}
                    />
                ))}
            </div>

            {/* Main Content */}
            <motion.div
                className="relative z-10 text-center px-6 sm:px-8 md:px-12 w-full max-w-6xl mx-auto"
                style={{ opacity, y: y2 }}
            >
                {/* Unique Top Ornament */}
                <motion.div
                    className="flex items-center justify-center gap-3 mb-8"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                >
                    <motion.div
                        className="flex items-center gap-2"
                        animate={{ rotate: [0, 5, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <div className="w-12 sm:w-16 h-[1px] bg-gradient-to-r from-transparent to-wedding-primary"></div>
                        <div className="relative">
                            <div className="w-3 h-3 bg-wedding-primary rounded-full"></div>
                            <div className="absolute inset-0 w-3 h-3 bg-wedding-primary rounded-full animate-ping"></div>
                        </div>
                        <div className="w-3 h-3 bg-wedding-accent rounded-full"></div>
                    </motion.div>

                    <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                        <svg width="30" height="30" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 sm:w-10 sm:h-10">
                            <circle cx="50" cy="50" r="20" fill="none" stroke="#C9A581" strokeWidth="1" />
                            <circle cx="50" cy="50" r="15" fill="none" stroke="#8B7355" strokeWidth="1" />
                            <circle cx="50" cy="50" r="3" fill="#C9A581" />
                        </svg>
                    </motion.div>

                    <motion.div
                        className="flex items-center gap-2"
                        animate={{ rotate: [0, -5, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <div className="w-3 h-3 bg-wedding-accent rounded-full"></div>
                        <div className="relative">
                            <div className="w-3 h-3 bg-wedding-primary rounded-full"></div>
                            <div className="absolute inset-0 w-3 h-3 bg-wedding-primary rounded-full animate-ping"></div>
                        </div>
                        <div className="w-12 sm:w-16 h-[1px] bg-gradient-to-l from-transparent to-wedding-primary"></div>
                    </motion.div>
                </motion.div>

                {/* Names with Gradient Text Effect - FIXED SIZING */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 0.4 }}
                    className="mb-6"
                >
                    <h1 className="relative overflow-hidden">
                        <span className="font-script text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl bg-gradient-to-r from-wedding-accent via-wedding-primary to-wedding-accent bg-clip-text text-transparent inline-block leading-tight px-4">
                            Praful & Aayushi
                        </span>
                        {/* Underline Effect */}
                        <motion.div
                            className="h-[2px] bg-gradient-to-r from-transparent via-wedding-primary to-transparent mx-auto mt-4 max-w-[80%] sm:max-w-[70%] md:max-w-[60%]"
                            initial={{ width: 0 }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 1.5, delay: 1 }}
                        />
                    </h1>
                </motion.div>

                {/* Elegant Divider */}
                <motion.div
                    className="flex items-center justify-center gap-3 sm:gap-4 my-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                >
                    <div className="relative">
                        <div className="w-16 sm:w-20 h-[1px] bg-wedding-primary"></div>
                        <motion.div
                            className="absolute top-1/2 left-0 w-full h-[1px] bg-wedding-accent"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 2, delay: 1.5, repeat: Infinity, repeatDelay: 3 }}
                        />
                    </div>
                    <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <svg width="25" height="25" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 sm:w-8 sm:h-8">
                            <g transform="translate(50, 50)">
                                <path
                                    d="M -10 -5 Q -10 -15, -6 -18 L -5 -18 Q -4 -15, -4 -10 L -4 0 Q -4 3, -6 5 L -8 5 Q -10 3, -10 0 Z"
                                    fill="#C9A581"
                                />
                                <path
                                    d="M 10 -5 Q 10 -15, 6 -18 L 5 -18 Q 4 -15, 4 -10 L 4 0 Q 4 3, 6 5 L 8 5 Q 10 3, 10 0 Z"
                                    fill="#C9A581"
                                />
                                <ellipse cx="0" cy="-5" rx="5" ry="13" fill="#C9A581" />
                            </g>
                        </svg>
                    </motion.div>
                    <div className="relative">
                        <div className="w-16 sm:w-20 h-[1px] bg-wedding-primary"></div>
                        <motion.div
                            className="absolute top-1/2 left-0 w-full h-[1px] bg-wedding-accent"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 2, delay: 1.5, repeat: Infinity, repeatDelay: 3 }}
                        />
                    </div>
                </motion.div>

                {/* Tagline with Letter Animation */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                >
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-wedding-charcoal font-light tracking-[0.2em] sm:tracking-[0.3em] mb-8">
                        {Array.from("CELEBRATING LOVE").map((letter, i) => (
                            <motion.span
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 1.2 + i * 0.05 }}
                            >
                                {letter === " " ? "\u00A0" : letter}
                            </motion.span>
                        ))}
                    </p>
                </motion.div>

                {/* Date Card with Glass Effect */}
                <motion.div
                    className="inline-block mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.5 }}
                >
                    <div className="bg-white/40 backdrop-blur-md border border-white/60 rounded-lg px-6 sm:px-8 py-4 sm:py-6 shadow-2xl">
                        <div className="flex items-center gap-4 sm:gap-6 text-wedding-accent">
                            <div className="text-center">
                                <p className="text-2xl sm:text-3xl font-serif font-bold">05</p>
                                <p className="text-[10px] sm:text-xs uppercase tracking-widest">February</p>
                            </div>
                            <div className="w-[1px] h-10 sm:h-12 bg-wedding-primary/30"></div>
                            <div className="text-center">
                                <p className="text-2xl sm:text-3xl font-serif font-bold">2026</p>
                                <p className="text-[10px] sm:text-xs uppercase tracking-widest">Delhi</p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Premium CTA Button */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.8 }}
                >
                    <a
                        href="#rsvp"
                        className="group relative inline-block"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-wedding-accent to-wedding-primary rounded-none transform transition-transform group-hover:scale-105"></div>
                        <div className="relative px-8 sm:px-12 py-4 sm:py-5 bg-wedding-accent text-white text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium transition-all duration-300 group-hover:bg-transparent">
                            We'd Love To See You
                            <motion.span
                                className="inline-block ml-2"
                                animate={{ x: [0, 5, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            >
                                →
                            </motion.span>
                        </div>
                    </a>
                </motion.div>
            </motion.div>

            {/* Premium Scroll Indicator */}
            <motion.div
                className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
            >
                <motion.div
                    className="flex flex-col items-center gap-3"
                    animate={{ y: [0, 12, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <div className="w-[1px] h-12 sm:h-16 bg-gradient-to-b from-transparent via-wedding-primary to-transparent"></div>
                    <div className="text-[10px] sm:text-xs text-wedding-accent uppercase tracking-[0.2em] sm:tracking-[0.3em] font-medium">
                        Scroll
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
